import { Link, useParams } from 'react-router-dom';
import { SEO } from '@/components/SEO';
import { getPost, relatedPosts } from '@/content/blog';
import { renderMarkdown } from '@/lib/renderMarkdown';

export function BlogPostPage() {
  const { slug } = useParams<{ slug: string }>();
  const post = slug ? getPost(slug) : undefined;

  if (!post) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-16 text-center">
        <h1 className="font-serif text-3xl text-ink-900">Post not found</h1>
        <p className="text-ink-600 mt-3">That article doesn’t exist (or was moved).</p>
        <Link to="/blog/" className="inline-block mt-6 text-accent-600 font-medium hover:text-accent-700">← Back to the blog</Link>
      </div>
    );
  }

  const related = relatedPosts(post.slug, 3);
  const url = `https://acescommerce.com/blog/${post.slug}/`;

  return (
    <div className="bg-ink-50">
      <SEO
        title={`${post.title} | ACES Blog`}
        description={post.excerpt}
        canonical={url}
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'BlogPosting',
          headline: post.title,
          description: post.excerpt,
          articleSection: post.category,
          keywords: post.tags.join(', '),
          url,
          publisher: { '@type': 'Organization', name: 'ACES', url: 'https://acescommerce.com/' },
        }}
      />

      <article className="max-w-2xl mx-auto px-4 pt-10 sm:pt-14 pb-20">
        <nav className="text-xs text-ink-500 mb-5">
          <Link to="/" className="underline-offset-4 hover:text-ink-900 hover:underline">Home</Link> ·{' '}
          <Link to="/blog/" className="underline-offset-4 hover:text-ink-900 hover:underline">Blog</Link> ·{' '}
          <span className="text-ink-500">{post.category}</span>
        </nav>

        <div className="flex items-center gap-2 mb-3">
          <span className="text-[10px] font-semibold uppercase tracking-[0.1em] text-accent-700 bg-accent-50 border border-accent-200/70 px-2 py-0.5 rounded-full">{post.category}</span>
          <span className="text-xs text-ink-500">{post.readMins} min read</span>
        </div>

        <h1 className="font-serif text-[2.1rem] sm:text-[2.75rem] text-ink-900 tracking-tight leading-[1.1] text-balance">{post.title}</h1>
        <p className="text-lg sm:text-[1.2rem] text-ink-600 mt-5 leading-relaxed text-pretty">{post.excerpt}</p>

        {post.image && (
          <img
            src={post.image}
            alt=""
            className="mt-8 w-full rounded-card-lg border border-ink-200/80 bg-cream shadow-card"
          />
        )}

        <div className="aces-rule mt-8 mb-8" />

        {/* Body */}
        <div className="aces-prose max-w-none">
          {renderMarkdown(post.body)}
        </div>

        {post.figuresCheckedOn && (
          <p className="mt-6 text-xs text-ink-500 leading-relaxed">
            Salary and fee figures in this article were checked against published sources on {post.figuresCheckedOn}. Pay varies by employer, city and experience.
          </p>
        )}

        {/* Tags */}
        {post.tags.length > 0 && (
          <div className="mt-8 flex flex-wrap gap-2">
            {post.tags.map((t) => (
              <span key={t} className="text-xs text-ink-500 bg-cream border border-ink-200 rounded-full px-2.5 py-1">#{t}</span>
            ))}
          </div>
        )}

        {/* CTA */}
        <div className="mt-12 aces-card p-7 sm:p-8 text-center">
          <h2 className="font-serif text-xl text-ink-900">Learn it free on ACES</h2>
          <p className="text-sm text-ink-600 mt-2 max-w-md mx-auto">Interactive notes, videos and quizzes for Plus One &amp; Plus Two Accountancy, Business Studies and Economics — no login, no fee.</p>
          <Link to="/" className="aces-btn-primary mt-6">Start learning →</Link>
        </div>
      </article>

      {/* Related */}
      {related.length > 0 && (
        <section className="bg-cream border-t border-ink-200">
          <div className="max-w-2xl mx-auto px-4 py-10">
            <h2 className="font-serif text-lg text-ink-900 mb-4">Keep reading</h2>
            <div className="grid grid-cols-1 gap-3">
              {related.map((r) => (
                <Link key={r.slug} to={`/blog/${r.slug}/`} className="group aces-card-hover flex items-center justify-between gap-4 rounded-card border border-ink-200/80 bg-ink-50/70 px-4 py-3.5 hover:border-accent-500/40">
                  <span className="min-w-0">
                    <span className="block font-medium text-ink-900 text-sm group-hover:text-accent-700 transition-colors duration-150 truncate">{r.title}</span>
                    <span className="block text-xs text-ink-500 mt-0.5">{r.category} · {r.readMins} min</span>
                  </span>
                  <span aria-hidden="true" className="text-ink-400 group-hover:text-accent-600 shrink-0">→</span>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
